<template>
    <div class="dynamic-content">
        <h1 class="page-title">Intellectual Property Policy</h1>
        <p class="effective-date">Effective Date: September 10, 2025</p>

        <div class="content-section compact-section">
            <p class="desc-text">XIGFOR INC. ("XigFor", "we", "us", or "our") respects intellectual property rights and is committed to protecting the original works, systems, and technologies developed for the XigFor platform.</p>
            <p class="desc-text">This Intellectual Property Policy explains the ownership, scope, and permitted use of intellectual property related to XigFor.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">1. Platform-Owned Intellectual Property</h2>
            <p class="desc-text">Unless otherwise stated, all intellectual property associated with the XigFor platform is owned by XIGFOR INC., including but not limited to:</p>
            <p class="desc-text bullet-text">The XigFor name, logo, icons, and visual identity</p>
            <p class="desc-text bullet-text">Platform interface designs, layouts, and user experience elements</p>
            <p class="desc-text bullet-text">The SmartRewards™ program structure, including its rules logic, progress mechanisms, and non-cash incentive design</p>
            <p class="desc-text bullet-text">Platform software, source code, backend systems, APIs, databases, and related technical architecture</p>
            <p class="desc-text bullet-text">Official documentation, policy texts, and platform-generated content</p>
            <p class="desc-text bullet-text">These materials are protected under applicable intellectual property laws, including copyright, trademark, and trade secret laws.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">2. Limited License to Users</h2>
            <p class="desc-text">XIGFOR INC. grants users a limited, non-exclusive, non-transferable, and revocable license to access and use the platform solely for its intended personal or commercial marketplace purposes, in accordance with XigFor's Terms of Service.</p>
            <p class="desc-text">This license does not grant users any ownership rights in the platform's intellectual property.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">3. User-Generated Content</h2>
            <p class="desc-text">Users may upload content such as product listings, descriptions, images, comments, or reviews ("User Content").</p>
            <p class="desc-text bullet-text">Users retain ownership of their own User Content.</p>
            <p class="desc-text bullet-text">By submitting User Content, users grant XIGFOR INC. a worldwide, royalty-free, non-exclusive license to host, display, reproduce, and distribute such content solely for platform operation, moderation, promotion, and service improvement purposes.</p>
            <p class="desc-text bullet-text">XIGFOR INC. does not claim ownership over User Content.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">4. Prohibited Use</h2>
            <p class="desc-text">Users may not, without prior written permission from XIGFOR INC.:</p>
            <p class="desc-text bullet-text">Copy, modify, reverse engineer, decompile, or attempt to extract platform source code or system logic</p>
            <p class="desc-text bullet-text">Use XigFor's branding, icons, or designs for commercial purposes outside the platform</p>
            <p class="desc-text bullet-text">Create derivative products or services that replicate XigFor's proprietary systems or incentive mechanisms</p>
            <p class="desc-text bullet-text">Misrepresent affiliation with XigFor or misuse its intellectual property in a misleading or unlawful manner</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">5. Third-Party Intellectual Property</h2>
            <p class="desc-text">XigFor may display content, trademarks, or services owned by third parties.</p>
            <p class="desc-text">All such intellectual property remains the property of its respective owners and is used in accordance with applicable licenses or legal permissions.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">6. Reporting Intellectual Property Concerns</h2>
            <p class="desc-text">If you believe that any content on XigFor infringes intellectual property rights, please contact us with relevant details.</p>
            <p class="desc-text">XIGFOR INC. will review reports in accordance with applicable laws and platform policies.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">7. Policy Updates</h2>
            <p class="desc-text">This Intellectual Property Policy may be updated from time to time to reflect platform development, legal requirements, or operational changes.</p>
            <p class="desc-text">Continued use of the platform after updates constitutes acceptance of the revised policy.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">Contact Information</h2>
            <p class="desc-text">For intellectual property inquiries, please contact:</p>
            <p class="desc-text">info@xigfor.com</p>
        </div>
    </div>
</template>

<script>
export default {
    name: 'IntellectualPropertyPolicy'
}
</script>

<style scoped>
@import '../info/shared-info-styles.css';

.dynamic-content {
    width: 100%;
}

.page-title {
    margin-bottom: 6px;
    font-size: 40px;
    font-weight: 700;
    color: #393944;
}

.effective-date {
    font-size: 40px;
    font-weight: 700;
    color: #9B9CA5;
    margin-bottom: 14px;
    line-height: 1.5;
}

.compact-section {
    margin-bottom: 14px;
}

.section-title {
    font-family: 'Poppins Light', sans-serif;
    font-size: 26px;
    font-weight: 300;
    line-height: 56px;
    color: #6C6C78;
    margin-top: 0;
    margin-bottom: 6px;
}

.desc-text {
    font-family: 'Poppins Light', sans-serif;
    font-size: 26px;
    font-weight: 300;
    line-height: 56px;
    color: #6C6C78;
    margin-bottom: 2px;
}

.bullet-text {
    position: relative;
    padding-left: 12px;
}

.bullet-text::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.72em;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: currentColor;
}

@media (max-width: 900px) {
    .page-title {
        font-size: 20px;
        font-weight: 700;
        color: #393944;
        margin-bottom: 4px;
        line-height: 1.3;
    }

    .effective-date {
        font-size: 12px;
        font-weight: 700;
        color: #9B9CA5;
        margin-bottom: 10px;
    }

    .section-title {
        font-family: 'Poppins Light', sans-serif;
        font-size: 10px;
        font-weight: 300;
        line-height: 20px;
        color: #6C6C78;
        margin-bottom: 5px;
    }

    .desc-text {
        font-family: 'Poppins Light', sans-serif;
        font-size: 10px;
        font-weight: 300;
        line-height: 20px;
        color: #6C6C78;
        margin-bottom: 2px;
    }

    .compact-section {
        margin-bottom: 12px;
    }

    .bullet-text {
        padding-left: 10px;
    }

    .bullet-text::before {
        top: 0.7em;
        width: 3px;
        height: 3px;
    }
}
</style>
